/**
 * ResolvedThemeProvider
 *
 * Auth-gated resolver that loads the active account's theme, merges it with
 * the base theme (account → base → Emtrain defaults), and renders a nested
 * ThemeProvider so all protected routes inherit the resolved brand tokens.
 *
 * Semantic guarantees:
 * - No theme queries fire before authentication.
 * - Defaults render immediately; the account overlay is applied once data loads
 *   (accepted FOUC).
 * - On client-context switch the previous account's theme is discarded at once;
 *   it is never carried forward to the new context.
 * - Custom fonts are applied only after successful async registration; load
 *   failures fall back to base/default. Stale completions (URL or accountId no
 *   longer current) are silently ignored.
 * - Account query error → fall back to base; base query error → fall back to
 *   Emtrain defaults.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  AssistantProvider,
  resolveAssistantSettingsForTheme,
  ThemeProvider,
  deepMergePartials,
  type DeepPartial,
  type ThemeTokens,
} from '@course-authoring/shared-types';
import { useAuth } from '../auth/AuthContext';
import { useClientContext, toScopeAccountId } from '../hooks/useClientContext';
import { useAccountTheme } from '../api/accountThemeApi';
import { adaptAccountThemeDto } from './accountThemeAdapter';
import { mapAccountThemeToTokens } from './accountThemeMapping';
import { themeFontFamilyName, themeFontCss, fontFaceDescriptors, loadAndRegisterFont } from './customFontLoader';

interface ResolvedThemeProviderProps {
  children: ReactNode;
}

export function ResolvedThemeProvider({ children }: ResolvedThemeProviderProps) {
  const { isAuthenticated } = useAuth();
  const { clientContext } = useClientContext();
  const accountId = toScopeAccountId(clientContext?.id);

  // ── Queries ────────────────────────────────────────────────────────────────
  // Base query always runs (when authenticated); account query only when there
  // is a non-null accountId so we don't fire a duplicate base query.
  const { data: baseDto } = useAccountTheme(null, { enabled: isAuthenticated });
  const { data: accountDto } = useAccountTheme(accountId, {
    enabled: isAuthenticated && accountId !== null,
  });

  // ── Custom-font state ──────────────────────────────────────────────────────
  // Track the resolved custom-font CSS stack after successful registration.
  // Reset to null on accountId change so stale completions are discarded.
  const [resolvedCustomFamily, setResolvedCustomFamily] = useState<string | null>(null);

  // Keep a ref of the "current" font URL so stale async completions can be
  // identified and ignored.
  const pendingFontUrlRef = useRef<string | null>(null);
  const pendingAccountIdRef = useRef<number | null>(accountId);

  // Reset custom-font state whenever the account context changes.
  useEffect(() => {
    if (pendingAccountIdRef.current !== accountId) {
      pendingAccountIdRef.current = accountId;
      pendingFontUrlRef.current = null;
      setResolvedCustomFamily(null);
    }
  }, [accountId]);

  // ── Resolve sync token overrides ───────────────────────────────────────────
  // Map base and account DTOs to token partials (omitting nulls).
  // Memoized so adapter results are stable across re-renders when data hasn't changed.
  const basePartial = useMemo(
    () => (baseDto ? mapAccountThemeToTokens(adaptAccountThemeDto(baseDto)) : {}),
    [baseDto],
  );

  const rawAccountAdapter = useMemo(
    () => (accountDto ? adaptAccountThemeDto(accountDto) : null),
    [accountDto],
  );

  // Build the synchronous account partial. fontFamily is excluded here for
  // custom fonts — it is added after async registration (see effect below).
  const accountPartialSync = useMemo(
    () =>
      rawAccountAdapter
        ? mapAccountThemeToTokens({
            ...rawAccountAdapter,
            // Exclude custom font from sync path; it comes in async.
            fontFamily: rawAccountAdapter.requiresCustomFont ? null : rawAccountAdapter.fontFamily,
          })
        : {},
    [rawAccountAdapter],
  );

  // Merge: base partial first, then account partial overrides, then custom font.
  const merged = useMemo<DeepPartial<ThemeTokens>>(() => {
    let result = deepMergePartials(basePartial, accountPartialSync);

    if (resolvedCustomFamily !== null) {
      result = deepMergePartials(result, { typography: { fontFamily: resolvedCustomFamily } });
    }

    return result;
  }, [basePartial, accountPartialSync, resolvedCustomFamily]);

  // Assistant settings are a complete resource and follow whole-theme
  // ownership. Keep them out of the token deep merge above.
  const assistantThemeDto = accountId === null ? baseDto : (accountDto ?? baseDto);
  const assistantSettings = resolveAssistantSettingsForTheme(assistantThemeDto);

  // ── Custom-font async registration ────────────────────────────────────────
  useEffect(() => {
    if (!rawAccountAdapter?.requiresCustomFont) {
      // No custom font needed — clear any previous resolved family.
      setResolvedCustomFamily(null);
      pendingFontUrlRef.current = null;
      return;
    }

    const { customFontUrl, customFontAssetId, customFontMetadata } = rawAccountAdapter;
    if (!customFontUrl || customFontAssetId === null) return;

    // Already in-flight or completed for this URL.
    if (pendingFontUrlRef.current === customFontUrl) return;

    pendingFontUrlRef.current = customFontUrl;
    const capturedAccountId = accountId;
    const capturedUrl = customFontUrl;

    const familyName = themeFontFamilyName(customFontAssetId);

    loadAndRegisterFont(
      familyName,
      `url(${customFontUrl})`,
      fontFaceDescriptors(customFontMetadata),
    ).then(() => {
      // Discard if context has changed since this load started.
      if (
        pendingFontUrlRef.current !== capturedUrl ||
        pendingAccountIdRef.current !== capturedAccountId
      ) {
        return;
      }
      setResolvedCustomFamily(themeFontCss(familyName));
    }).catch(() => {
      // Load failed — omit the custom font; base/default wins.
      if (pendingFontUrlRef.current === capturedUrl) {
        pendingFontUrlRef.current = null;
      }
    });
  }, [rawAccountAdapter, accountId]);

  return (
    <AssistantProvider
      assistant={assistantSettings}
      avatarUrl={assistantThemeDto?.assistantAvatar?.url ?? null}
      translations={assistantThemeDto?.assistantTranslations}
    >
      <ThemeProvider theme={merged}>
        {children}
      </ThemeProvider>
    </AssistantProvider>
  );
}
